import { useCallback, useEffect } from 'react';
import {
  Control,
  FieldError,
  FieldPathByValue,
  FieldValues,
  get,
  UseFormClearErrors,
  UseFormSetError,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/shared/api';
import { toastError } from '@/shared/lib';
import { formatPasswordRefusal, getPasswordRulesFromError, isPasswordTooLong } from './passwordRules';

type Params<TValues extends FieldValues> = {
  control: Control<TValues>;
  setError: UseFormSetError<TValues>;
  clearErrors: UseFormClearErrors<TValues>;
  name: FieldPathByValue<TValues, string | undefined>;
};

// Тип ошибки, которую поле показывает при вводе, до первой попытки сохранения
const TOO_LONG_WHILE_TYPING = 'tooLongWhileTyping';

/**
 * Правила надёжности у поля нового пароля формы: показ отказа сервера и сообщение о превышении длины
 * сразу при вводе. Проверку до отправки форма берёт у `usePasswordStrengthValidator` — правилом
 * поля или своей схемой.
 *
 * До первой попытки сохранения форма поле не проверяет, поэтому о превышении длины сообщает сам хук —
 * только о нём: прочие причины называются при сохранении. После попытки сохранения поле
 * перепроверяется формой при каждой правке, и превышение длины входит в полный текст отказа.
 */
export const usePasswordRulesField = <TValues extends FieldValues>({
  control,
  setError,
  clearErrors,
  name,
}: Params<TValues>) => {
  const { t } = useTranslation('common');
  const password = (useWatch({ control, name }) as string | undefined) ?? '';
  const { errors, isSubmitted } = useFormState({ control, name });

  const isTooLong = isPasswordTooLong(password);
  const hasTooLongError = (get(errors, name) as FieldError | undefined)?.type === TOO_LONG_WHILE_TYPING;

  useEffect(() => {
    if (isSubmitted) {
      return;
    }
    if (isTooLong && !hasTooLongError) {
      setError(name, { type: TOO_LONG_WHILE_TYPING, message: formatPasswordRefusal(['maxLength'], t) });
    } else if (!isTooLong && hasTooLongError) {
      clearErrors(name);
    }
  }, [clearErrors, hasTooLongError, isSubmitted, isTooLong, name, setError, t]);

  /** Показывает у поля отказ сервера по правилам надёжности; `false` — отказ не по ним */
  const applyServerRefusal = useCallback(
    (error: unknown): boolean => {
      const rules = getPasswordRulesFromError(error);
      if (!rules) {
        return false;
      }
      setError(name, { type: 'server', message: formatPasswordRefusal(rules, t) }, { shouldFocus: true });
      return true;
    },
    [name, setError, t],
  );

  /** Показывает отказ сервера: по правилам надёжности — у поля, прочий — уведомлением с причиной из ответа */
  const showServerRefusal = useCallback(
    (error: unknown) => {
      if (!applyServerRefusal(error)) {
        toastError(t('error'), { description: getApiErrorMessage(error) });
      }
    },
    [applyServerRefusal, t],
  );

  return { applyServerRefusal, showServerRefusal };
};
